import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Collapse,
  Link,
  Autocomplete,
  TextField,
  Checkbox,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  Select,
  MenuItem,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  HourglassEmpty as PendingIcon,
  Error as ErrorIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Block as AbortIcon,
  DeleteOutline as DeleteIcon,
  ReportProblemOutlined as MarkFailedIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { testCasesAPI, modulesAPI } from '../services/api';
import { API_BASE_URL } from '../utils/apiConfig';
import { formatUtcToLocal, utcToLocalDate } from '../utils/dateUtils';
import ResizableTableCell from '../components/ResizableTableCell';
import TruncatedText from '../components/TruncatedText';
import ScenarioExamplesTable from '../components/ScenarioExamplesTable';

// =============================================================================
// Helpers
// =============================================================================
const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'pass': return <PassIcon color="success" />;
    case 'fail': return <FailIcon color="error" />;
    case 'pending': return <PendingIcon color="warning" />;
    case 'skipped': return <PendingIcon color="disabled" />;
    case 'aborted': return <AbortIcon color="error" />;
    default: return <ErrorIcon color="disabled" />;
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pass': return 'success';
    case 'fail': return 'error';
    case 'pending': return 'warning';
    case 'aborted': return 'error';
    default: return 'default';
  }
};

const getRunStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'success';
    case 'running': return 'info';
    case 'pending': return 'warning';
    case 'failed': return 'error';
    default: return 'default';
  }
};

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return 'N/A';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const formatDateTime = (dt) => dt ? formatUtcToLocal(dt) : 'N/A';

const calcRunDuration = (run) => {
  if (!run.started_at) return 'N/A';
  const start = utcToLocalDate(run.started_at);
  const end = run.completed_at ? utcToLocalDate(run.completed_at) : new Date();
  const secs = Math.round((end - start) / 1000);
  return formatDuration(secs);
};

// =============================================================================
// Main Component
// =============================================================================
const Executions = () => {
  // ---- URL Search Params (for deep linking from AI assistant) ----
  const [searchParams] = useSearchParams();

  // ---- Common State ----
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // ---- Tab 1: Select & Run ----
  const [testCases, setTestCases] = useState([]);
  const [modules, setModules] = useState([]);
  const [loadingTestCases, setLoadingTestCases] = useState(false);
  const [filters, setFilters] = useState({
    test_id: '', title: '', module_id: '', sub_module: '',
    feature_section: '', tag: '', tags: '', automation_status: '',
  });
  const [filterSubModules, setFilterSubModules] = useState([]);
  const [filterFeatures, setFilterFeatures] = useState([]);
  const [selectedForExecution, setSelectedForExecution] = useState(new Set());
  const [expandedTestCase, setExpandedTestCase] = useState(null);
  const [tcPage, setTcPage] = useState(0);
  const [tcRowsPerPage, setTcRowsPerPage] = useState(25);

  // Execute dialog
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [releases, setReleases] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState('');
  const [executing, setExecuting] = useState(false);
  const [loadingReleases, setLoadingReleases] = useState(false);

  // ---- Tab 2: Execution Results ----
  const [testRuns, setTestRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [runsPage, setRunsPage] = useState(0);
  const [runsRowsPerPage, setRunsRowsPerPage] = useState(10);
  // Orphan (individual) executions
  const [executions, setExecutions] = useState([]);
  const [orphanPage, setOrphanPage] = useState(0);
  const [orphanRowsPerPage, setOrphanRowsPerPage] = useState(10);

  // Execution details dialog
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showStdout, setShowStdout] = useState(false);

  // Live run
  const [activeRunId, setActiveRunId] = useState(null);
  const [runProgress, setRunProgress] = useState(null);
  const [runReportUrl, setRunReportUrl] = useState(null);
  const eventSourceRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Action-in-progress
  const [actionInProgress, setActionInProgress] = useState({});

  // ---- Derived Data ----
  const automatedTestCases = useMemo(
    () => testCases.filter(tc => tc.test_type?.toLowerCase() === 'automated'),
    [testCases]
  );

  // Client-side filtering for Tab 1
  const filteredTestCases = useMemo(() => {
    return automatedTestCases.filter(tc => {
      if (filters.test_id && !tc.test_id?.toLowerCase().includes(filters.test_id.toLowerCase())) return false;
      if (filters.title && !tc.title?.toLowerCase().includes(filters.title.toLowerCase())) return false;
      if (filters.module_id && tc.module_id !== parseInt(filters.module_id)) return false;
      if (filters.sub_module && tc.sub_module !== filters.sub_module) return false;
      if (filters.feature_section && tc.feature_section !== filters.feature_section) return false;
      if (filters.tag && tc.tag !== filters.tag) return false;
      if (filters.tags && !tc.tags?.toLowerCase().includes(filters.tags.toLowerCase())) return false;
      if (filters.automation_status && tc.automation_status !== filters.automation_status) return false;
      return true;
    });
  }, [automatedTestCases, filters]);

  // Orphan executions (no test_run_id)
  const orphanExecutions = useMemo(
    () => executions.filter(e => !e.test_run_id),
    [executions]
  );

  // Run stats for Tab 2
  const runStats = useMemo(() => {
    const totalRuns = testRuns.length;
    let totalTests = 0, passed = 0, failed = 0, pending = 0;
    testRuns.forEach(r => {
      totalTests += r.total_tests || 0;
      passed += r.passed || 0;
      failed += r.failed || 0;
      pending += (r.total_tests || 0) - (r.passed || 0) - (r.failed || 0) - (r.skipped || 0);
    });
    return { totalRuns, totalTests, passed, failed, pending };
  }, [testRuns]);

  // ---- Data Fetching ----
  const fetchTestCases = useCallback(async () => {
    setLoadingTestCases(true);
    try {
      const data = await testCasesAPI.getAll();
      setTestCases(data);
    } catch (err) {
      console.error('Error fetching test cases:', err);
    } finally {
      setLoadingTestCases(false);
    }
  }, []);

  const fetchModules = useCallback(async () => {
    try {
      const data = await modulesAPI.getAll();
      setModules(data);
    } catch (err) {
      console.error('Error fetching modules:', err);
    }
  }, []);

  const fetchReleases = useCallback(async () => {
    setLoadingReleases(true);
    try {
      const response = await api.get('/releases');
      setReleases(response.data);
    } catch (err) {
      console.error('Error fetching releases:', err);
    } finally {
      setLoadingReleases(false);
    }
  }, []);

  const fetchTestRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const response = await api.get('/executions/runs/', { params: { limit: 50 } });
      setTestRuns(response.data);
    } catch (err) {
      console.error('Error fetching test runs:', err);
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  const fetchExecutions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/executions');
      setExecutions(response.data);
    } catch (err) {
      console.error('Error fetching executions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---- SSE ----
  const connectSSE = useCallback((testRunId) => {
    if (eventSourceRef.current) eventSourceRef.current.close();

    const baseUrl = API_BASE_URL;
    const url = `${baseUrl}/api/executions/stream/${testRunId}`;
    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('update', (event) => {
      try {
        const data = JSON.parse(event.data);
        setExecutions(prev => prev.map(exec =>
          exec.id === data.execution_id
            ? { ...exec, status: data.status, actual_result: data.actual_result, error_message: data.error_message, execution_time: data.execution_time }
            : exec
        ));
        setRunProgress({
          passed: data.passed || 0,
          failed: data.failed || 0,
          skipped: data.skipped || 0,
          total: data.total || 0,
        });
      } catch (err) { console.error('SSE update error:', err); }
    });

    eventSource.addEventListener('completed', (event) => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      try {
        const data = JSON.parse(event.data);
        setRunProgress({
          passed: data.passed || 0,
          failed: data.failed || 0,
          skipped: data.skipped || 0,
          total: data.total || 0,
        });
        setRunReportUrl(data.report_url || null);
        setActiveRunId(null);
        if (Array.isArray(data.executions)) {
          setExecutions(prev => prev.map(exec => {
            const updated = data.executions.find(e => e.execution_id === exec.id);
            return updated ? { ...exec, status: updated.status, execution_time: updated.execution_time } : exec;
          }));
        }
      } catch (err) { console.error('SSE completed error:', err); }
      eventSource.close();
      eventSourceRef.current = null;
      fetchExecutions();
      fetchTestRuns();
    });

    eventSource.onerror = () => {
      eventSource.close();
      eventSourceRef.current = null;
      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(async () => {
          await fetchExecutions();
          try {
            const res = await api.get('/executions/runs/', { params: { limit: 5 } });
            const stillActive = res.data.find(r => r.status === 'pending' || r.status === 'running');
            if (!stillActive) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
              setActiveRunId(null);
              fetchTestRuns();
            }
          } catch (_) {}
        }, 10000);
      }
    };
  }, [fetchExecutions, fetchTestRuns]);

  // ---- Mount ----
  useEffect(() => {
    fetchTestCases();
    fetchModules();
    fetchReleases();
    fetchExecutions();
    fetchTestRuns();

    const resumeActiveRun = async () => {
      try {
        const res = await api.get('/executions/runs/', { params: { limit: 5 } });
        const activeRun = res.data.find(r => r.status === 'pending' || r.status === 'running');
        if (activeRun) {
          setActiveRunId(activeRun.id);
          setRunProgress({
            passed: activeRun.passed || 0,
            failed: activeRun.failed || 0,
            skipped: activeRun.skipped || 0,
            total: activeRun.total_tests || 0,
          });
          connectSSE(activeRun.id);
        }
      } catch (err) { console.error('Resume active run error:', err); }
    };
    resumeActiveRun();

    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Apply URL search params (deep linking from AI assistant) ----
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const runIdParam = searchParams.get('run_id');

    if (tabParam === 'results') {
      setActiveTab(1);
    }
    if (runIdParam) {
      const rid = parseInt(runIdParam, 10);
      if (!isNaN(rid)) {
        setExpandedRunId(rid);
        // Also switch to results tab
        setActiveTab(1);
      }
    }
  }, [searchParams]);

  // ---- Filter helpers (cascading dropdowns) ----
  const loadFilterSubModules = async (moduleId) => {
    if (!moduleId) { setFilterSubModules([]); setFilterFeatures([]); return; }
    try {
      const options = await testCasesAPI.getHierarchyOptions(moduleId);
      setFilterSubModules(options);
      setFilterFeatures([]);
    } catch { setFilterSubModules([]); }
  };

  const loadFilterFeatures = async (moduleId, subModule) => {
    if (!moduleId || !subModule) { setFilterFeatures([]); return; }
    try {
      const options = await testCasesAPI.getHierarchyOptions(moduleId, subModule);
      setFilterFeatures(options);
    } catch { setFilterFeatures([]); }
  };

  const handleFilterChange = async (name, value) => {
    const newFilters = { ...filters, [name]: value };
    if (name === 'module_id') {
      newFilters.sub_module = '';
      newFilters.feature_section = '';
      await loadFilterSubModules(value);
    } else if (name === 'sub_module') {
      newFilters.feature_section = '';
      await loadFilterFeatures(filters.module_id, value);
    }
    setFilters(newFilters);
    setTcPage(0);
  };

  // ---- Tab 1 Checkbox Handlers ----
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedForExecution(new Set(filteredTestCases.map(tc => tc.id)));
    } else {
      setSelectedForExecution(new Set());
    }
  };

  const handleSelectTestCase = (id) => {
    const next = new Set(selectedForExecution);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedForExecution(next);
  };

  // ---- Execute Tests ----
  const handleOpenExecuteDialog = () => {
    fetchReleases();
    setSelectedRelease('');
    setExecuteDialogOpen(true);
  };

  const handleCloseExecuteDialog = () => {
    setExecuteDialogOpen(false);
    setSelectedRelease('');
  };

  const handleExecuteTests = async () => {
    if (selectedForExecution.size === 0 || !selectedRelease) {
      setSnackbar({ open: true, message: 'Please select test cases and a release', severity: 'warning' });
      return;
    }
    setExecuting(true);
    try {
      const response = await api.post('/executions/execute-batch', {
        test_case_ids: Array.from(selectedForExecution),
        release_id: selectedRelease,
      });
      const { test_run_id, execution_ids } = response.data;

      handleCloseExecuteDialog();
      setActiveRunId(test_run_id);
      setRunProgress({ passed: 0, failed: 0, skipped: 0, total: execution_ids.length });
      setRunReportUrl(null);

      setSnackbar({ open: true, message: `Execution started with ${execution_ids.length} test case(s)`, severity: 'success' });
      setSelectedForExecution(new Set());
      await fetchExecutions();
      await fetchTestRuns();
      connectSSE(test_run_id);
      setActiveTab(1); // Switch to results tab
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to execute: ' + (error.response?.data?.detail || error.message), severity: 'error' });
    } finally {
      setExecuting(false);
    }
  };

  // ---- Execution Actions ----
  const handleViewDetails = (execution) => {
    setSelectedExecution(execution);
    setShowStdout(false);
    setDetailsOpen(true);
  };

  const withAction = (executionId, fn) => async (e) => {
    if (e) e.stopPropagation();
    if (actionInProgress[executionId]) return;
    setActionInProgress(prev => ({ ...prev, [executionId]: true }));
    try { await fn(); } finally {
      setActionInProgress(prev => { const n = { ...prev }; delete n[executionId]; return n; });
    }
  };

  const handleAbort = (execution) => withAction(execution.id, async () => {
    try {
      await api.post(`/executions/${execution.id}/abort`);
      setExecutions(prev => prev.map(e => e.id === execution.id ? { ...e, status: 'aborted' } : e));
      setSnackbar({ open: true, message: `Execution #${execution.id} aborted`, severity: 'warning' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to abort: ' + (err.response?.data?.detail || err.message), severity: 'error' });
    }
  });

  const handleDeleteExecution = (execution) => withAction(execution.id, async () => {
    try {
      await api.delete(`/executions/${execution.id}`);
      setExecutions(prev => prev.filter(e => e.id !== execution.id));
      fetchTestRuns();
      setSnackbar({ open: true, message: `Execution #${execution.id} deleted`, severity: 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete: ' + (err.response?.data?.detail || err.message), severity: 'error' });
    }
  });

  const handleMarkFailed = (execution) => withAction(execution.id, async () => {
    try {
      const res = await api.post(`/executions/${execution.id}/mark-failed`);
      setExecutions(prev => prev.map(e => e.id === execution.id ? { ...e, status: res.data.status } : e));
      setSnackbar({ open: true, message: `Execution #${execution.id} marked as failed`, severity: 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update: ' + (err.response?.data?.detail || err.message), severity: 'error' });
    }
  });

  const [deleteRunInProgress, setDeleteRunInProgress] = useState({});
  const handleDeleteRun = (runId) => async (e) => {
    if (e) e.stopPropagation();
    if (deleteRunInProgress[runId]) return;
    if (!window.confirm(`Delete Run #${runId} and all its executions? This cannot be undone.`)) return;
    setDeleteRunInProgress(prev => ({ ...prev, [runId]: true }));
    try {
      await api.delete(`/executions/runs/${runId}`);
      setTestRuns(prev => prev.filter(r => r.id !== runId));
      setExecutions(prev => prev.filter(e => e.test_run_id !== runId));
      if (expandedRunId === runId) setExpandedRunId(null);
      setSnackbar({ open: true, message: `Run #${runId} deleted`, severity: 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete run: ' + (err.response?.data?.detail || err.message), severity: 'error' });
    } finally {
      setDeleteRunInProgress(prev => { const n = { ...prev }; delete n[runId]; return n; });
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Live progress derived values
  const completedCount = runProgress ? runProgress.passed + runProgress.failed + runProgress.skipped : 0;
  const progressPercent = runProgress && runProgress.total > 0 ? Math.round((completedCount / runProgress.total) * 100) : 0;

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <Box sx={{ p: 3 }}>
      {/* ===== Header ===== */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">Test Executions</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchTestCases(); fetchTestRuns(); fetchExecutions();
            }}
          >
            Refresh
          </Button>
          {activeTab === 0 && (
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              disabled={selectedForExecution.size === 0}
              onClick={handleOpenExecuteDialog}
            >
              Execute Selected ({selectedForExecution.size})
            </Button>
          )}
        </Box>
      </Box>

      {/* ===== Live Run Progress Panel ===== */}
      {(activeRunId || runProgress) && (
        <Paper sx={{ p: 2, mb: 2, border: activeRunId ? '2px solid' : '1px solid', borderColor: activeRunId ? 'primary.main' : 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {activeRunId ? (
                <>
                  <Box component="span" sx={{
                    display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                    bgcolor: 'primary.main',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                  }} />
                  Test Run in Progress
                </>
              ) : 'Last Run Results'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {runProgress && (
                <>
                  <Chip label={`${runProgress.passed} Passed`} color="success" size="small" variant="outlined" />
                  <Chip label={`${runProgress.failed} Failed`} color="error" size="small" variant="outlined" />
                  <Chip label={`${runProgress.skipped} Skipped`} size="small" variant="outlined" />
                  <Typography variant="body2" color="textSecondary">{completedCount} / {runProgress.total}</Typography>
                </>
              )}
              {runReportUrl && (
                <Tooltip title="View GitHub Actions Run">
                  <Link href={runReportUrl} target="_blank" rel="noopener noreferrer">
                    <IconButton size="small" color="primary"><OpenInNewIcon /></IconButton>
                  </Link>
                </Tooltip>
              )}
              {!activeRunId && runProgress && (
                <Button size="small" onClick={() => { setRunProgress(null); setRunReportUrl(null); }}>Dismiss</Button>
              )}
            </Box>
          </Box>
          {activeRunId && (
            <LinearProgress variant="determinate" value={progressPercent} sx={{ height: 8, borderRadius: 4 }} />
          )}
        </Paper>
      )}

      {/* ===== Tabs ===== */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`Select & Run (${automatedTestCases.length})`} />
        <Tab label={`Execution Results (${testRuns.length})`} />
      </Tabs>

      {/* ================================================================= */}
      {/* TAB 0: SELECT & RUN                                               */}
      {/* ================================================================= */}
      {activeTab === 0 && (
        <Paper>
          {loadingTestCases && <LinearProgress />}
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1200, tableLayout: 'fixed' }}>
              <TableHead>
                {/* Header Row */}
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 50 }}>
                    <Checkbox
                      indeterminate={selectedForExecution.size > 0 && selectedForExecution.size < filteredTestCases.length}
                      checked={filteredTestCases.length > 0 && selectedForExecution.size === filteredTestCases.length}
                      onChange={handleSelectAll}
                      color="primary"
                    />
                  </TableCell>
                  <ResizableTableCell minWidth={100} initialWidth={120} isHeader>Test ID</ResizableTableCell>
                  <ResizableTableCell minWidth={200} initialWidth={250} isHeader>Title</ResizableTableCell>
                  <ResizableTableCell minWidth={130} initialWidth={150} isHeader>Module</ResizableTableCell>
                  <ResizableTableCell minWidth={130} initialWidth={150} isHeader>Sub-Module</ResizableTableCell>
                  <ResizableTableCell minWidth={130} initialWidth={150} isHeader>Feature</ResizableTableCell>
                  <ResizableTableCell minWidth={80} initialWidth={100} isHeader>Tag</ResizableTableCell>
                  <ResizableTableCell minWidth={140} initialWidth={160} isHeader>Tags</ResizableTableCell>
                  <ResizableTableCell minWidth={100} initialWidth={110} isHeader>Status</ResizableTableCell>
                  <ResizableTableCell minWidth={50} initialWidth={60} isHeader></ResizableTableCell>
                </TableRow>

                {/* Filter Row */}
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    <TextField size="small" placeholder="Filter..." value={filters.test_id}
                      onChange={(e) => setFilters(f => ({ ...f, test_id: e.target.value }))} fullWidth
                      variant="standard" />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" placeholder="Filter..." value={filters.title}
                      onChange={(e) => setFilters(f => ({ ...f, title: e.target.value }))} fullWidth
                      variant="standard" />
                  </TableCell>
                  <TableCell>
                    <Select size="small" value={filters.module_id}
                      onChange={(e) => handleFilterChange('module_id', e.target.value)}
                      displayEmpty fullWidth variant="standard">
                      <MenuItem value=""><em>All</em></MenuItem>
                      {modules.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select size="small" value={filters.sub_module}
                      onChange={(e) => handleFilterChange('sub_module', e.target.value)}
                      displayEmpty fullWidth variant="standard" disabled={!filters.module_id}>
                      <MenuItem value=""><em>All</em></MenuItem>
                      {filterSubModules.map(sm => <MenuItem key={sm.name} value={sm.name}>{sm.name}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select size="small" value={filters.feature_section}
                      onChange={(e) => handleFilterChange('feature_section', e.target.value)}
                      displayEmpty fullWidth variant="standard"
                      disabled={!filters.module_id || !filters.sub_module}>
                      <MenuItem value=""><em>All</em></MenuItem>
                      {filterFeatures.map(f => <MenuItem key={f.name} value={f.name}>{f.name}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select size="small" value={filters.tag}
                      onChange={(e) => setFilters(f => ({ ...f, tag: e.target.value }))}
                      displayEmpty fullWidth variant="standard">
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="ui">UI</MenuItem>
                      <MenuItem value="api">API</MenuItem>
                      <MenuItem value="hybrid">Hybrid</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField size="small" placeholder="Filter tags..." value={filters.tags}
                      onChange={(e) => setFilters(f => ({ ...f, tags: e.target.value }))} fullWidth
                      variant="standard" />
                  </TableCell>
                  <TableCell>
                    <Select size="small" value={filters.automation_status}
                      onChange={(e) => setFilters(f => ({ ...f, automation_status: e.target.value }))}
                      displayEmpty fullWidth variant="standard">
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="working">Working</MenuItem>
                      <MenuItem value="broken">Broken</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredTestCases
                  .slice(tcPage * tcRowsPerPage, tcPage * tcRowsPerPage + tcRowsPerPage)
                  .map((tc) => (
                    <React.Fragment key={tc.id}>
                      <TableRow hover sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedForExecution.has(tc.id)}
                            onChange={() => handleSelectTestCase(tc.id)}
                            color="primary"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell
                          onClick={() => setExpandedTestCase(expandedTestCase === tc.id ? null : tc.id)}
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'primary.main', fontWeight: 500, cursor: 'pointer' }}
                        >
                          {tc.test_id}
                        </TableCell>
                        <TableCell
                          onClick={() => setExpandedTestCase(expandedTestCase === tc.id ? null : tc.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TruncatedText text={tc.title || ''} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tc.module?.name || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tc.sub_module || '\u2014'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tc.feature_section || '\u2014'}
                        </TableCell>
                        <TableCell>
                          {tc.tag && <Chip label={tc.tag} size="small" variant="outlined" />}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tc.tags ? tc.tags.split(',').map((tag, i) => (
                            <Chip key={i} label={tag.trim()} size="small" variant="outlined" sx={{ mr: 0.3, mb: 0.3 }} />
                          )) : '\u2014'}
                        </TableCell>
                        <TableCell>
                          {tc.automation_status && (
                            <Chip
                              label={tc.automation_status}
                              size="small"
                              color={tc.automation_status === 'working' ? 'success' : 'error'}
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedTestCase(expandedTestCase === tc.id ? null : tc.id)}
                          >
                            {expandedTestCase === tc.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* Accordion Detail Row */}
                      {expandedTestCase === tc.id && (
                        <TableRow>
                          <TableCell colSpan={10} sx={{ py: 0, px: 0, border: 0 }}>
                            <Box sx={{ bgcolor: 'grey.50', p: 3 }}>
                              <Grid container spacing={2}>
                                {tc.description && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Description</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{tc.description}</Typography>
                                  </Grid>
                                )}
                                {tc.preconditions && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Preconditions</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{tc.preconditions}</Typography>
                                  </Grid>
                                )}
                                {tc.steps_to_reproduce && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Steps to Reproduce</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{tc.steps_to_reproduce}</Typography>
                                  </Grid>
                                )}
                                {tc.expected_result && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Expected Result</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{tc.expected_result}</Typography>
                                  </Grid>
                                )}
                                {tc.automated_script_path && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>Script Path</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                                      {tc.automated_script_path}
                                    </Typography>
                                  </Grid>
                                )}
                                {tc.scenario_examples && (() => {
                                  try {
                                    const parsed = typeof tc.scenario_examples === 'string' ? JSON.parse(tc.scenario_examples) : tc.scenario_examples;
                                    if (parsed && (parsed.headers || parsed.rows)) {
                                      return (
                                        <Grid item xs={12}>
                                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>Scenario Examples</Typography>
                                          <ScenarioExamplesTable data={parsed} readOnly />
                                        </Grid>
                                      );
                                    }
                                  } catch { /* ignore */ }
                                  return null;
                                })()}
                                {(tc.jira_story_id || tc.jira_epic_id) && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>JIRA</Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      {tc.jira_story_id && <Chip label={`Story: ${tc.jira_story_id}`} size="small" variant="outlined" />}
                                      {tc.jira_epic_id && <Chip label={`Epic: ${tc.jira_epic_id}`} size="small" variant="outlined" />}
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}

                {filteredTestCases.length === 0 && !loadingTestCases && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                        No automated test cases found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredTestCases.length}
            rowsPerPage={tcRowsPerPage}
            page={tcPage}
            onPageChange={(_, p) => setTcPage(p)}
            onRowsPerPageChange={(e) => { setTcRowsPerPage(parseInt(e.target.value, 10)); setTcPage(0); }}
          />
        </Paper>
      )}

      {/* ================================================================= */}
      {/* TAB 1: EXECUTION RESULTS                                          */}
      {/* ================================================================= */}
      {activeTab === 1 && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {[
              { label: 'Total Runs', value: runStats.totalRuns, sub: `${runStats.totalTests} total tests`, bg: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: '#fff' },
              { label: 'Passed', value: runStats.passed, bg: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)', color: '#fff' },
              { label: 'Failed', value: runStats.failed, bg: 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)', color: '#fff' },
              { label: 'Pending', value: runStats.pending < 0 ? 0 : runStats.pending, bg: 'linear-gradient(135deg, #e65100 0%, #ffa726 100%)', color: '#fff' },
            ].map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.label}>
                <Card sx={{ background: card.bg, color: card.color, height: '100%', borderRadius: 2, boxShadow: 3 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 80, py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, fontSize: '0.8rem', mb: 0.25 }}>{card.label}</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{card.value}</Typography>
                    {card.sub && <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', mt: 0.25 }}>{card.sub}</Typography>}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Test Runs Table */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6">Test Runs</Typography>
            </Box>
            {loadingRuns && <LinearProgress />}
            <TableContainer>
              <Table size="small" sx={{ minWidth: 1000, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <ResizableTableCell minWidth={80} initialWidth={90} isHeader>Run ID</ResizableTableCell>
                    <ResizableTableCell minWidth={120} initialWidth={140} isHeader>Release</ResizableTableCell>
                    <ResizableTableCell minWidth={70} initialWidth={80} isHeader># Tests</ResizableTableCell>
                    <ResizableTableCell minWidth={90} initialWidth={110} isHeader>Status</ResizableTableCell>
                    <ResizableTableCell minWidth={160} initialWidth={200} isHeader>Results</ResizableTableCell>
                    <ResizableTableCell minWidth={120} initialWidth={140} isHeader>Triggered By</ResizableTableCell>
                    <ResizableTableCell minWidth={160} initialWidth={180} isHeader>Started</ResizableTableCell>
                    <ResizableTableCell minWidth={100} initialWidth={110} isHeader>Duration</ResizableTableCell>
                    <ResizableTableCell minWidth={80} initialWidth={90} isHeader>Actions</ResizableTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {testRuns
                    .slice(runsPage * runsRowsPerPage, runsPage * runsRowsPerPage + runsRowsPerPage)
                    .map((run) => {
                      const isExpanded = expandedRunId === run.id;
                      const isActive = activeRunId === run.id;
                      return (
                        <React.Fragment key={run.id}>
                          <TableRow
                            hover
                            sx={{
                              cursor: 'pointer',
                              ...(isActive && { bgcolor: 'action.selected' }),
                            }}
                            onClick={() => setExpandedRunId(isExpanded ? null : run.id)}
                          >
                            <TableCell sx={{ width: 40, p: 0.5 }}>
                              <IconButton size="small">
                                {isExpanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>#{run.id}</TableCell>
                            <TableCell>{run.release?.version || run.release?.name || 'N/A'}</TableCell>
                            <TableCell>{run.total_tests}</TableCell>
                            <TableCell>
                              <Chip
                                label={run.status || 'unknown'}
                                size="small"
                                color={getRunStatusColor(run.status)}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {run.passed > 0 && <Chip label={`${run.passed} \u2713`} size="small" color="success" variant="outlined" sx={{ height: 22 }} />}
                                {run.failed > 0 && <Chip label={`${run.failed} \u2717`} size="small" color="error" variant="outlined" sx={{ height: 22 }} />}
                                {run.skipped > 0 && <Chip label={`${run.skipped} \u2298`} size="small" variant="outlined" sx={{ height: 22 }} />}
                                {run.passed === 0 && run.failed === 0 && run.skipped === 0 && (
                                  <Typography variant="caption" color="textSecondary">{'\u2014'}</Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {run.trigger_user?.full_name || 'System'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {formatDateTime(run.started_at)}
                            </TableCell>
                            <TableCell>{calcRunDuration(run)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {run.report_url && (
                                  <Tooltip title="View Report">
                                    <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); window.open(run.report_url, '_blank'); }}>
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Delete Run">
                                  <span>
                                    <IconButton size="small" color="error"
                                      disabled={!!deleteRunInProgress[run.id]}
                                      onClick={handleDeleteRun(run.id)}>
                                      {deleteRunInProgress[run.id] ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Expanded: per-execution sub-table */}
                          <TableRow>
                            <TableCell colSpan={10} sx={{ py: 0, px: 0, border: isExpanded ? undefined : 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ mx: 2, my: 1 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Test Executions in Run #{run.id}
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell sx={{ fontWeight: 600, width: 80 }}>Exec ID</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Test Case</TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: 120 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: 100 }}>Duration</TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: 140 }}>Actions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(run.executions || []).map((exec) => (
                                        <TableRow key={exec.id} hover>
                                          <TableCell>{exec.id}</TableCell>
                                          <TableCell>
                                            <Typography variant="body2" noWrap>
                                              {exec.test_case?.test_id ? `${exec.test_case.test_id} \u2014 ` : ''}
                                              {exec.test_case?.title || 'N/A'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              icon={getStatusIcon(exec.status)}
                                              label={exec.status || 'Unknown'}
                                              color={getStatusColor(exec.status)}
                                              size="small"
                                            />
                                          </TableCell>
                                          <TableCell>{formatDuration(exec.execution_time)}</TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                              <Tooltip title="View Details">
                                                <IconButton size="small" onClick={() => handleViewDetails(exec)}>
                                                  <ViewIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                              {exec.report_url && (
                                                <Tooltip title="View Report">
                                                  <IconButton size="small" color="primary" onClick={() => window.open(exec.report_url, '_blank')}>
                                                    <OpenInNewIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                              )}
                                              {exec.status?.toLowerCase() === 'pending' && (
                                                <>
                                                  <Tooltip title="Abort">
                                                    <span>
                                                      <IconButton size="small" color="error"
                                                        disabled={!!actionInProgress[exec.id]}
                                                        onClick={handleAbort(exec)}>
                                                        {actionInProgress[exec.id] ? <CircularProgress size={14} /> : <AbortIcon fontSize="small" />}
                                                      </IconButton>
                                                    </span>
                                                  </Tooltip>
                                                  <Tooltip title="Mark as Failed">
                                                    <span>
                                                      <IconButton size="small" color="warning"
                                                        disabled={!!actionInProgress[exec.id]}
                                                        onClick={handleMarkFailed(exec)}>
                                                        <MarkFailedIcon fontSize="small" />
                                                      </IconButton>
                                                    </span>
                                                  </Tooltip>
                                                </>
                                              )}
                                              <Tooltip title="Delete">
                                                <span>
                                                  <IconButton size="small"
                                                    disabled={!!actionInProgress[exec.id]}
                                                    onClick={handleDeleteExecution(exec)}>
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                      {(!run.executions || run.executions.length === 0) && (
                                        <TableRow>
                                          <TableCell colSpan={5} align="center">
                                            <Typography variant="caption" color="textSecondary">No executions found for this run</Typography>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}

                  {testRuns.length === 0 && !loadingRuns && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                          No test runs found. Go to "Select & Run" to execute tests.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={testRuns.length}
              rowsPerPage={runsRowsPerPage}
              page={runsPage}
              onPageChange={(_, p) => setRunsPage(p)}
              onRowsPerPageChange={(e) => { setRunsRowsPerPage(parseInt(e.target.value, 10)); setRunsPage(0); }}
            />
          </Paper>

          {/* Orphan (Individual) Executions */}
          {orphanExecutions.length > 0 && (
            <Paper>
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6">Individual Executions</Typography>
                <Typography variant="caption" color="textSecondary">
                  Executions not part of a batch run
                </Typography>
              </Box>
              {loading && <LinearProgress />}
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1000, tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <ResizableTableCell minWidth={80} initialWidth={100} isHeader>Exec ID</ResizableTableCell>
                      <ResizableTableCell minWidth={200} initialWidth={250} isHeader>Test Case</ResizableTableCell>
                      <ResizableTableCell minWidth={120} initialWidth={140} isHeader>Release</ResizableTableCell>
                      <ResizableTableCell minWidth={100} initialWidth={120} isHeader>Status</ResizableTableCell>
                      <ResizableTableCell minWidth={100} initialWidth={120} isHeader>Runner</ResizableTableCell>
                      <ResizableTableCell minWidth={150} initialWidth={170} isHeader>Executed By</ResizableTableCell>
                      <ResizableTableCell minWidth={160} initialWidth={180} isHeader>Executed At</ResizableTableCell>
                      <ResizableTableCell minWidth={90} initialWidth={100} isHeader>Duration</ResizableTableCell>
                      <ResizableTableCell minWidth={100} initialWidth={120} isHeader>Actions</ResizableTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orphanExecutions
                      .slice(orphanPage * orphanRowsPerPage, orphanPage * orphanRowsPerPage + orphanRowsPerPage)
                      .map((execution) => (
                        <TableRow key={execution.id} hover onClick={() => handleViewDetails(execution)} sx={{ cursor: 'pointer' }}>
                          <TableCell>{execution.id}</TableCell>
                          <TableCell>
                            <TruncatedText text={execution.test_case?.title || execution.test_case?.test_case_id || 'N/A'} />
                          </TableCell>
                          <TableCell>{execution.release?.version || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip icon={getStatusIcon(execution.status)} label={execution.status || 'Unknown'} color={getStatusColor(execution.status)} size="small" />
                          </TableCell>
                          <TableCell>
                            {execution.runner_type === 'github_actions'
                              ? <Chip label="GitHub" size="small" variant="outlined" color="info" />
                              : <Chip label="Local" size="small" variant="outlined" />}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {execution.executor?.full_name || 'System'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {formatDateTime(execution.executed_at)}
                          </TableCell>
                          <TableCell>{formatDuration(execution.execution_time)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View Details">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewDetails(execution); }}>
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {execution.status?.toLowerCase() === 'pending' && (
                                <>
                                  <Tooltip title="Abort">
                                    <span>
                                      <IconButton size="small" color="error" disabled={!!actionInProgress[execution.id]} onClick={handleAbort(execution)}>
                                        {actionInProgress[execution.id] ? <CircularProgress size={14} /> : <AbortIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Mark as Failed">
                                    <span>
                                      <IconButton size="small" color="warning" disabled={!!actionInProgress[execution.id]} onClick={handleMarkFailed(execution)}>
                                        <MarkFailedIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <span>
                                      <IconButton size="small" disabled={!!actionInProgress[execution.id]} onClick={handleDeleteExecution(execution)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={orphanExecutions.length}
                rowsPerPage={orphanRowsPerPage}
                page={orphanPage}
                onPageChange={(_, p) => setOrphanPage(p)}
                onRowsPerPageChange={(e) => { setOrphanRowsPerPage(parseInt(e.target.value, 10)); setOrphanPage(0); }}
              />
            </Paper>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Execute Tests Dialog (Release picker only)                         */}
      {/* ================================================================= */}
      <Dialog open={executeDialogOpen} onClose={handleCloseExecuteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Execute Tests</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {selectedForExecution.size} automated test case(s) selected
            </Alert>
            <Autocomplete
              options={releases}
              getOptionLabel={(option) => `${option.version} - ${option.name}`}
              value={releases.find(r => r.id === selectedRelease) || null}
              onChange={(_, newValue) => setSelectedRelease(newValue?.id || '')}
              loading={loadingReleases}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Release"
                  placeholder="Search releases..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingReleases ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExecuteDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleExecuteTests}
            disabled={executing || !selectedRelease}
            startIcon={executing ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
          >
            {executing ? 'Starting...' : 'Execute'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================= */}
      {/* Execution Details Dialog                                           */}
      {/* ================================================================= */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Execution Details</DialogTitle>
        <DialogContent>
          {selectedExecution && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Execution ID</Typography>
                  <Typography variant="body1">{selectedExecution.id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip icon={getStatusIcon(selectedExecution.status)} label={selectedExecution.status} color={getStatusColor(selectedExecution.status)} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Test Case</Typography>
                  <Typography variant="body1">
                    {selectedExecution.test_case?.test_id || selectedExecution.test_case?.test_case_id} - {selectedExecution.test_case?.title}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Release</Typography>
                  <Typography variant="body1">
                    {selectedExecution.release?.version} - {selectedExecution.release?.name || ''}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Executed By</Typography>
                  <Typography variant="body1">{selectedExecution.executor?.full_name || 'System'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Executed At</Typography>
                  <Typography variant="body1">{formatDateTime(selectedExecution.executed_at)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Duration</Typography>
                  <Typography variant="body1">{formatDuration(selectedExecution.execution_time)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Runner</Typography>
                  <Typography variant="body1">{selectedExecution.runner_type === 'github_actions' ? 'GitHub Actions' : 'Local'}</Typography>
                </Grid>
                {selectedExecution.report_url && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Report</Typography>
                    <Link href={selectedExecution.report_url} target="_blank" rel="noopener noreferrer">
                      View GitHub Actions Run <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5 }} />
                    </Link>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Error Message</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {selectedExecution.error_message || 'No errors'}
                    </Typography>
                  </Paper>
                </Grid>
                {selectedExecution.stdout && (
                  <Grid item xs={12}>
                    <Button size="small" onClick={() => setShowStdout(!showStdout)} startIcon={showStdout ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                      {showStdout ? 'Hide' : 'Show'} Test Output
                    </Button>
                    <Collapse in={showStdout}>
                      <Paper sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', mt: 1, maxHeight: 400, overflow: 'auto' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {selectedExecution.stdout}
                        </Typography>
                      </Paper>
                    </Collapse>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Executions;
